import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { F_NUM, F_STR } from 'assets/FormatNumbers'
import { searchLog, select } from 'types/SearchTypes'
import { getNowSetTime, getNumberFromDate, getNumberFromDateTimeString } from 'utils/dateUtil'
import { convertNumber, fixNullValue } from 'utils/numberUtil'

type SearchLogState = {
  searchLog: searchLog // 販売条件照会画面検索情報
  JIGYO_NM_ERROR: boolean // 事業所名が取得できているか
  TOKUI_NM_ERROR: boolean // 得意先名が取得できているか
  TEN_NM_ERROR: boolean // 店舗名が取得できているか
  NAME_ERROR: boolean // 氏名が取得できているか
  ADD_NAME_ERROR: boolean // 担当者名が取得できているか
  initView: number // 初期描画フラグ
  jigyoList: select[] // 事業所リスト
}

const initJoken = {
  ADD_YMD_FROM: getNumberFromDate(getNowSetTime(0, 0, 0), 14), // 年月日FROM
  ADD_YMD_TO: getNumberFromDate(getNowSetTime(23, 59, 59), 14), // 年月日FROM
  JIGYO_CD: F_NUM, // 事業所コード
  JIGYO_NM: F_STR, // 事業所名
  TOROKU_KBN: {
    torokuCheck: true, // 登録チェックボックス
    sakujoCheck: true, // 削除チェックボックス
    koshinCheck: true, // 更新チェックボックス
  },
  ADD_MAIL: F_STR, // 登録者メールアドレス
  ADD_NAME: F_STR, // 登録者名
  TOKUI_CD: F_NUM, // 得意先コード
  TOKUI_NM: F_STR, // 得意先名
  TEN_CD: F_NUM, // 店舗コード
  TEN_NM: F_STR, // 店舗名
  MAIL: F_STR, // メールアドレス
  NAME: F_STR, // 名前
}

const initialState: SearchLogState = {
  searchLog: initJoken, // 販売条件照会画面検索情報
  JIGYO_NM_ERROR: false, // 事業所名が取得できているか
  TOKUI_NM_ERROR: false, // 得意先名が取得できているか
  TEN_NM_ERROR: false, // 店舗名が取得できているか
  NAME_ERROR: false, // 氏名が取得できているか
  ADD_NAME_ERROR: false, // 担当者名が取得できているか
  initView: 0, // 初期描画フラグ
  jigyoList: [], // 事業所リスト
}
export const SearchLogSlice = createSlice({
  name: 'SearchLog',
  initialState: initialState,
  reducers: {
    // 事業所リストを変更する処理 //===========================//
    changeJigyoList: (state, action: PayloadAction<select[]>) => {
      state.jigyoList = action.payload
    },
    // 初期描画フラグを変更する処理 //===========================//
    changeInitView: (state, action: PayloadAction<number>) => {
      state.initView = action.payload
    },
    // 登録者名を取得できているかを変更する処理 //===========================//
    changeAddNameError: (state, action: PayloadAction<boolean>) => {
      state.ADD_NAME_ERROR = action.payload
    },
    // 氏名を取得できているかを変更する処理 //===========================//
    changeNameError: (state, action: PayloadAction<boolean>) => {
      state.NAME_ERROR = action.payload
    },
    // 店舗名を取得できているかを変更する処理 //===========================//
    changeTenpoError: (state, action: PayloadAction<boolean>) => {
      state.TEN_NM_ERROR = action.payload
    },
    // 得意先名を取得できているかを変更する処理 //===========================//
    changeTokuisakiError: (state, action: PayloadAction<boolean>) => {
      state.TOKUI_NM_ERROR = action.payload
    },
    // 事業所名を取得できているかを変更する処理 //===========================//
    changeJigyoshoError: (state, action: PayloadAction<boolean>) => {
      state.JIGYO_NM_ERROR = action.payload
    },
    // 名前を変更する処理 //===========================//
    changeName: (state, action: PayloadAction<string>) => {
      state.searchLog.NAME = action.payload
    },
    // 登録者名を変更する処理 //===========================//
    changeAddName: (state, action: PayloadAction<string>) => {
      state.searchLog.ADD_NAME = action.payload
    },
    // 登録者メールアドレスを変更する処理 //===========================//
    changeAddMail: (state, action: PayloadAction<string>) => {
      state.searchLog.ADD_MAIL = action.payload
    },
    // メールアドレスを変更する処理 //===========================//
    changeMail: (state, action: PayloadAction<string>) => {
      state.searchLog.MAIL = action.payload
    },
    // 店舗名を変更する処理 //===========================//
    changeTenpoName: (state, action: PayloadAction<string>) => {
      state.searchLog.TEN_NM = action.payload
    },
    // 店舗コードを変更する処理 //===========================//
    changeTenpoCode: (state, action: PayloadAction<string>) => {
      state.searchLog.TEN_CD = convertNumber(action.payload, 5)
    },
    // 得意先名を変更する処理 //===========================//
    changeTokuisakiName: (state, action: PayloadAction<string>) => {
      state.searchLog.TOKUI_NM = action.payload
    },
    // 得意先コードを変更する処理 //===========================//
    changeTokuisakiCode: (state, action: PayloadAction<string>) => {
      state.searchLog.TOKUI_CD = convertNumber(action.payload, 5)
    },
    // 事業所名を変更する処理 //===========================//
    changeJigyoshoName: (state, action: PayloadAction<string>) => {
      state.searchLog.JIGYO_NM = action.payload
    },
    // 事業所コードを変更する処理 //===========================//
    changeJigyoshoCode: (state, action: PayloadAction<string>) => {
      state.searchLog.JIGYO_CD = convertNumber(action.payload, 2)
    },
    // 更新チェックボックスを変更する処理 //===========================//
    changeKoshinCheck: (state) => {
      state.searchLog.TOROKU_KBN.koshinCheck = !state.searchLog.TOROKU_KBN.koshinCheck
    },
    // 削除チェックボックスを変更する処理 //===========================//
    changeSakujoCheck: (state) => {
      state.searchLog.TOROKU_KBN.sakujoCheck = !state.searchLog.TOROKU_KBN.sakujoCheck
    },
    // 登録チェックボックスを変更する処理 //===========================//
    changeTourokuCheck: (state) => {
      state.searchLog.TOROKU_KBN.torokuCheck = !state.searchLog.TOROKU_KBN.torokuCheck
    },
    // 日時Toを変更する処理 //===========================//
    changeAddYmdTo: (state, action: PayloadAction<string>) => {
      state.searchLog.ADD_YMD_TO = fixNullValue(getNumberFromDateTimeString(action.payload).toString())
    },
    // 日時FROMを変更する処理 //===========================//
    changeAddYmdFrom: (state, action: PayloadAction<string>) => {
      state.searchLog.ADD_YMD_FROM = fixNullValue(getNumberFromDateTimeString(action.payload).toString())
    },
    // 初期化する処理 //===========================//
    format: (state) => {
      for (const key in state) {
        const tempKey: keyof SearchLogState = key as keyof SearchLogState
        ;(state as any)[tempKey] = initialState[tempKey]
      }
    },
  },
})

// actionCreator 書き忘れないこと！
export const {
  changeJigyoList, // 事業所リストを変更する処理
  changeInitView, // 初期描画フラグを変更する処理
  changeAddNameError, // 登録者名を取得できているかを変更する処理
  changeNameError, // 氏名を取得できているかを変更する処理
  changeTenpoError, // 店舗名を取得できているかを変更する処理
  changeTokuisakiError, // 得意先名を取得できているかを変更する処理
  changeJigyoshoError, // 事業所名を取得できているかを変更する処理
  changeAddName, // 登録者名を変更する処理
  changeAddMail, // 登録者メールアドレスを変更する処理
  changeName, // 名前を変更する処理
  changeMail, // メールアドレスを変更する処理
  changeTenpoName, // 店舗名を変更する処理
  changeTenpoCode, // 店舗コードを変更する処理
  changeTokuisakiName, // 得意先名を変更する処理
  changeTokuisakiCode, // 得意先コードを変更する処理
  changeJigyoshoName, // 事業所名を変更する処理
  changeJigyoshoCode, // 事業所コードを変更する処理
  changeKoshinCheck, // 更新チェックボックスを変更する処理
  changeSakujoCheck, // 削除チェックボックスを変更する処理
  changeTourokuCheck, // 登録チェックボックスを変更する処理
  changeAddYmdTo, // 日時Toを変更する処理
  changeAddYmdFrom, // 日時FROMを変更する処理
  format, // 初期化する処理
} = SearchLogSlice.actions
export default SearchLogSlice.reducer
